/* eslint-disable @typescript-eslint/no-explicit-any */

type AnyRecord = Record<string, any>;

export type ChartTextGroupId =
    | 'title'
    | 'xAxis'
    | 'yAxis'
    | 'seriesLabel'
    | 'seriesEndLabel'
    | `graphicText:${number}:${number}`;

export type ChartTextGroup = {
    id: ChartTextGroupId;
    label: string;
    fontSize: number | null;
    fontFamily: string | null;
};

const GRAPHIC_TEXT_PREFIX = 'graphicText';

function getNumeric(node: AnyRecord | undefined, key: string): number | null {
    if (!node) return null;
    const raw = node[key];
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function getStr(node: AnyRecord | undefined, key: string): string | null {
    if (!node) return null;
    const raw = node[key];
    return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

const FONT_SHORTHAND_RE =
    /^\s*(?:(italic|oblique|normal)\s+)?(?:(bold|bolder|lighter|normal|[1-9]00)\s+)?(\d+(?:\.\d+)?)px\s+(.+?)\s*$/i;

type ParsedFontShorthand = {
    style: string | null;
    weight: string | null;
    fontSize: number;
    family: string;
};

function parseFontShorthand(value: unknown): ParsedFontShorthand | null {
    if (typeof value !== 'string') return null;
    const m = FONT_SHORTHAND_RE.exec(value);
    if (!m) return null;
    const [, style, weight, size, family] = m;
    return {
        style: style ?? null,
        weight: weight ?? null,
        fontSize: parseFloat(size),
        family,
    };
}

function buildFontShorthand(parts: ParsedFontShorthand): string {
    const segments: string[] = [];
    if (parts.style) segments.push(parts.style);
    if (parts.weight) segments.push(parts.weight);
    segments.push(`${parts.fontSize}px`);
    segments.push(parts.family);
    return segments.join(' ');
}

function asArray<T>(value: T | T[] | undefined): T[] {
    if (value === undefined || value === null) return [];
    return Array.isArray(value) ? value : [value];
}

function getAxisLabelStyles(echartsConfig: AnyRecord, axisKey: 'xAxis' | 'yAxis'): AnyRecord[] {
    const styles: AnyRecord[] = [];
    for (const axis of asArray<AnyRecord>(echartsConfig[axisKey])) {
        if (
            axis &&
            typeof axis === 'object' &&
            axis.axisLabel &&
            typeof axis.axisLabel === 'object'
        ) {
            styles.push(axis.axisLabel as AnyRecord);
        }
    }
    return styles;
}

function getSeriesTextStyles(echartsConfig: AnyRecord, field: 'label' | 'endLabel'): AnyRecord[] {
    const styles: AnyRecord[] = [];
    for (const series of asArray<AnyRecord>(echartsConfig.series)) {
        if (
            series &&
            typeof series === 'object' &&
            series[field] &&
            typeof series[field] === 'object'
        ) {
            styles.push(series[field] as AnyRecord);
        }
    }
    return styles;
}

function getTitleTextStyle(echartsConfig: AnyRecord): AnyRecord | null {
    const title = echartsConfig.title;
    if (!title || typeof title !== 'object') return null;
    if (title.textStyle && typeof title.textStyle === 'object') {
        return title.textStyle as AnyRecord;
    }
    return null;
}

type GraphicTextRef = {
    graphicIndex: number;
    childIndex: number;
    styleNode: AnyRecord;
    parsedFont: ParsedFontShorthand | null;
};

function getGraphicTextRefs(echartsConfig: AnyRecord): GraphicTextRef[] {
    const refs: GraphicTextRef[] = [];
    const graphics = asArray<AnyRecord>(echartsConfig.graphic);
    graphics.forEach((g, graphicIndex) => {
        if (!g || typeof g !== 'object') return;
        const children: AnyRecord[] = Array.isArray(g.children) ? g.children : [g];
        children.forEach((child, childIndex) => {
            if (!child || typeof child !== 'object') return;
            if (child.type !== 'text') return;
            const style = child.style;
            if (!style || typeof style !== 'object') return;
            const parsedFont = parseFontShorthand((style as AnyRecord).font);
            refs.push({
                graphicIndex,
                childIndex,
                styleNode: style as AnyRecord,
                parsedFont,
            });
        });
    });
    return refs;
}

function pickFontSizeFromStyles(styles: AnyRecord[]): number | null {
    for (const s of styles) {
        const size = getNumeric(s, 'fontSize');
        if (size !== null) return size;
    }
    return null;
}

function pickFontFamilyFromStyles(styles: AnyRecord[]): string | null {
    for (const s of styles) {
        const family = getStr(s, 'fontFamily');
        if (family !== null) return family;
    }
    return null;
}

/**
 * Walk a (resolved or template-ref) echarts config and produce one row per distinct
 * text group present in the config. The returned groups reflect what the user actually
 * sees and is editable. Some configs may yield e.g. {title, axisLabel}, others
 * {seriesLabel + graphicText 0..1}, etc — the number is config-dependent.
 */
export function discoverChartTextGroups(echartsConfig: AnyRecord | undefined): ChartTextGroup[] {
    if (!echartsConfig || typeof echartsConfig !== 'object') return [];

    const groups: ChartTextGroup[] = [];

    const titleStyle = getTitleTextStyle(echartsConfig);
    if (titleStyle) {
        groups.push({
            id: 'title',
            label: 'Title',
            fontSize: getNumeric(titleStyle, 'fontSize'),
            fontFamily: getStr(titleStyle, 'fontFamily'),
        });
    }

    const xAxisLabels = getAxisLabelStyles(echartsConfig, 'xAxis');
    if (xAxisLabels.length > 0) {
        groups.push({
            id: 'xAxis',
            label: 'X-axis labels',
            fontSize: pickFontSizeFromStyles(xAxisLabels),
            fontFamily: pickFontFamilyFromStyles(xAxisLabels),
        });
    }

    const yAxisLabels = getAxisLabelStyles(echartsConfig, 'yAxis');
    if (yAxisLabels.length > 0) {
        groups.push({
            id: 'yAxis',
            label: 'Y-axis labels',
            fontSize: pickFontSizeFromStyles(yAxisLabels),
            fontFamily: pickFontFamilyFromStyles(yAxisLabels),
        });
    }

    const seriesLabels = getSeriesTextStyles(echartsConfig, 'label');
    if (seriesLabels.length > 0) {
        groups.push({
            id: 'seriesLabel',
            label: 'Data labels',
            fontSize: pickFontSizeFromStyles(seriesLabels),
            fontFamily: pickFontFamilyFromStyles(seriesLabels),
        });
    }

    const seriesEndLabels = getSeriesTextStyles(echartsConfig, 'endLabel');
    if (seriesEndLabels.length > 0) {
        groups.push({
            id: 'seriesEndLabel',
            label: 'Series labels',
            fontSize: pickFontSizeFromStyles(seriesEndLabels),
            fontFamily: pickFontFamilyFromStyles(seriesEndLabels),
        });
    }

    const graphicRefs = getGraphicTextRefs(echartsConfig);
    graphicRefs.forEach((ref, orderIndex) => {
        const fontSize = ref.parsedFont?.fontSize ?? getNumeric(ref.styleNode, 'fontSize') ?? null;
        const fontFamily = ref.parsedFont?.family ?? getStr(ref.styleNode, 'fontFamily') ?? null;
        groups.push({
            id: `${GRAPHIC_TEXT_PREFIX}:${ref.graphicIndex}:${ref.childIndex}` as ChartTextGroupId,
            label: graphicTextLabel(ref, orderIndex),
            fontSize,
            fontFamily,
        });
    });

    return groups;
}

function graphicTextLabel(ref: GraphicTextRef, orderIndex: number): string {
    const size = ref.parsedFont?.fontSize ?? null;
    const weight = ref.parsedFont?.weight ?? null;
    if (size !== null && size >= 40 && (weight === 'bold' || weight === 'bolder')) {
        return 'Highlight number';
    }
    if (orderIndex === 0) return 'Caption';
    return `Text ${orderIndex + 1}`;
}

export type ChartTextStyleChange = {
    fontSize?: number;
    fontFamily?: string;
};

/**
 * Returns a new echarts config with `change` applied to every node belonging to `groupId`.
 * Returns the original config object identity if nothing changed.
 */
export function applyChartTextStyleChange(
    echartsConfig: AnyRecord,
    groupId: ChartTextGroupId,
    change: ChartTextStyleChange,
): AnyRecord {
    if (change.fontSize === undefined && change.fontFamily === undefined) {
        return echartsConfig;
    }

    const next: AnyRecord = { ...echartsConfig };
    let mutated = false;

    const mutateStyleNode = (style: AnyRecord): AnyRecord => {
        const updated: AnyRecord = { ...style };
        if (change.fontSize !== undefined) updated.fontSize = change.fontSize;
        if (change.fontFamily !== undefined) updated.fontFamily = change.fontFamily;
        mutated = true;
        return updated;
    };

    const mutateGraphicStyle = (style: AnyRecord): AnyRecord => {
        const parsed = parseFontShorthand((style as AnyRecord).font);
        if (parsed) {
            const updated: AnyRecord = { ...style };
            const newParts: ParsedFontShorthand = {
                ...parsed,
                fontSize: change.fontSize ?? parsed.fontSize,
                family: change.fontFamily ?? parsed.family,
            };
            updated.font = buildFontShorthand(newParts);
            mutated = true;
            return updated;
        }
        return mutateStyleNode(style);
    };

    switch (groupId) {
        case 'title': {
            const title = next.title;
            if (title && typeof title === 'object') {
                const ts = (title as AnyRecord).textStyle;
                if (ts && typeof ts === 'object') {
                    next.title = {
                        ...(title as AnyRecord),
                        textStyle: mutateStyleNode(ts as AnyRecord),
                    };
                }
            }

            break;
        }
        case 'xAxis':
        case 'yAxis': {
            const axes = next[groupId];
            const update = (axis: AnyRecord): AnyRecord => {
                if (!axis.axisLabel || typeof axis.axisLabel !== 'object') return axis;
                return { ...axis, axisLabel: mutateStyleNode(axis.axisLabel as AnyRecord) };
            };
            if (Array.isArray(axes)) {
                next[groupId] = axes.map((a) => (a && typeof a === 'object' ? update(a) : a));
            } else if (axes && typeof axes === 'object') {
                next[groupId] = update(axes as AnyRecord);
            }

            break;
        }
        case 'seriesLabel':
        case 'seriesEndLabel': {
            const field = groupId === 'seriesLabel' ? 'label' : 'endLabel';
            const series = next.series;
            if (Array.isArray(series)) {
                next.series = series.map((s: AnyRecord) => {
                    if (!s || typeof s !== 'object') return s;
                    const styleNode = s[field];
                    if (!styleNode || typeof styleNode !== 'object') return s;
                    return { ...s, [field]: mutateStyleNode(styleNode as AnyRecord) };
                });
            }

            break;
        }
        default:
            if (groupId.startsWith(`${GRAPHIC_TEXT_PREFIX}:`)) {
                const [, gIdxStr, cIdxStr] = groupId.split(':');
                const targetGIdx = Number(gIdxStr);
                const targetCIdx = Number(cIdxStr);
                const graphics = next.graphic;
                if (Array.isArray(graphics)) {
                    next.graphic = graphics.map((g: AnyRecord, gIdx: number) => {
                        if (!g || typeof g !== 'object' || gIdx !== targetGIdx) return g;
                        const children = (g as AnyRecord).children;
                        if (!Array.isArray(children)) return g;
                        const newChildren = children.map((child: AnyRecord, cIdx: number) => {
                            if (!child || typeof child !== 'object' || cIdx !== targetCIdx)
                                return child;
                            if (child.type !== 'text') return child;
                            const style = (child as AnyRecord).style;
                            if (!style || typeof style !== 'object') return child;
                            return { ...child, style: mutateGraphicStyle(style as AnyRecord) };
                        });
                        return { ...(g as AnyRecord), children: newChildren };
                    });
                }
            }
    }

    return mutated ? next : echartsConfig;
}
